import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import {
  adminApi,
  getCurrentAdmin,
  isAuthenticated as checkAuth,
  clearAdminAuth,
} from './services/api.js';
import type {
  AdminAccountDto,
  AdminStatsResponse,
  AdminUsersListResponse,
  AdminUserListItem,
  AdminMinersListResponse,
} from '@nova-mine/shared';

import { AdminHeader, type AdminTab } from './components/AdminHeader.js';
import { LoginModal } from './components/LoginModal.js';
import { DashboardView } from './components/DashboardView.js';
import { UserTable } from './components/UserTable.js';
import { UserDetailModal } from './components/UserDetailModal.js';
import { BalanceAdjustModal } from './components/BalanceAdjustModal.js';
import { MinersView } from './components/MinersView.js';
import { SettingsView } from './components/SettingsView.js';
import { PaymentsView } from './components/PaymentsView.js';
import { ActivityLogsView } from './components/ActivityLogsView.js';

export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(checkAuth());
  const [currentAdmin, setCurrentAdmin] = useState<AdminAccountDto | null>(getCurrentAdmin());
  const [currentTab, setCurrentTab] = useState<AdminTab>('dashboard');

  // Loading & Error States
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  // Tab Data States
  const [stats, setStats] = useState<AdminStatsResponse | null>(null);

  // Users Tab State
  const [usersData, setUsersData] = useState<AdminUsersListResponse | null>(null);
  const [usersOffset, setUsersOffset] = useState<number>(0);
  const [usersLimit] = useState<number>(25);
  const [userSearchQuery, setUserSearchQuery] = useState<string>('');
  const [userStatusFilter, setUserStatusFilter] = useState<string>('all');
  const [usersLoading, setUsersLoading] = useState<boolean>(false);

  // Miners Tab State
  const [minersData, setMinersData] = useState<AdminMinersListResponse | null>(null);
  const [minersOffset, setMinersOffset] = useState<number>(0);
  const [minersLimit] = useState<number>(25);
  const [minerTypeFilter, setMinerTypeFilter] = useState<string>('all');
  const [minerStatusFilter, setMinerStatusFilter] = useState<string>('all');
  const [minerSearchQuery, setMinerSearchQuery] = useState<string>('');
  const [minersLoading, setMinersLoading] = useState<boolean>(false);

  // Modals State
  const [inspectingUserId, setInspectingUserId] = useState<string | null>(null);
  const [adjustingUser, setAdjustingUser] = useState<AdminUserListItem | null>(null);

  // Verify authentication on mount
  useEffect(() => {
    async function verifyAuth() {
      if (checkAuth()) {
        try {
          const me = await adminApi.getMe();
          setCurrentAdmin(me.admin);
          setIsAuthenticated(true);
        } catch (err) {
          console.warn('[Session Expired or Invalid]', err);
          clearAdminAuth();
          setIsAuthenticated(false);
          setCurrentAdmin(null);
        }
      }
    }
    verifyAuth();
  }, []);

  // Fetch Dashboard Stats
  const loadDashboardStats = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setIsLoading(true);
      setGlobalError(null);
      const res = await adminApi.getStats();
      setStats(res);
    } catch (err: unknown) {
      console.error('[Load Stats Error]', err);
      setGlobalError(err instanceof Error ? err.message : 'Failed to fetch server statistics');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch Users
  const loadUsers = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setUsersLoading(true);
      const res = await adminApi.getUsers(
        usersLimit,
        usersOffset,
        userSearchQuery,
        userStatusFilter
      );
      setUsersData(res);
    } catch (err: unknown) {
      console.error('[Load Users Error]', err);
      setGlobalError(err instanceof Error ? err.message : 'Failed to load user accounts');
    } finally {
      setUsersLoading(false);
    }
  }, [isAuthenticated, usersLimit, usersOffset, userSearchQuery, userStatusFilter]);

  // Fetch Miners
  const loadMiners = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setMinersLoading(true);
      const res = await adminApi.getMiners(
        minersLimit,
        minersOffset,
        minerTypeFilter,
        minerStatusFilter,
        minerSearchQuery
      );
      setMinersData(res);
    } catch (err: unknown) {
      console.error('[Load Miners Error]', err);
      setGlobalError(err instanceof Error ? err.message : 'Failed to load miners fleet');
    } finally {
      setMinersLoading(false);
    }
  }, [isAuthenticated, minersLimit, minersOffset, minerTypeFilter, minerStatusFilter, minerSearchQuery]);

  // Trigger loads based on active tab
  useEffect(() => {
    if (!isAuthenticated) return;
    if (currentTab === 'dashboard') {
      loadDashboardStats();
    } else if (currentTab === 'users') {
      loadUsers();
    } else if (currentTab === 'miners') {
      loadMiners();
    }
  }, [isAuthenticated, currentTab, loadDashboardStats, loadUsers, loadMiners]);

  // Global Refresh Action
  const handleGlobalRefresh = () => {
    if (currentTab === 'dashboard') loadDashboardStats();
    else if (currentTab === 'users') loadUsers();
    else if (currentTab === 'miners') loadMiners();
  };

  // Auth Handlers
  const handleLoginSuccess = (admin: AdminAccountDto) => {
    setCurrentAdmin(admin);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await adminApi.logout();
    } finally {
      clearAdminAuth();
      setIsAuthenticated(false);
      setCurrentAdmin(null);
    }
  };

  // User Actions
  const handleCommitAdjust = async (userId: string, amount: number, reason: string) => {
    await adminApi.adjustBalance(userId, amount, reason);
    await loadUsers();
    if (currentTab === 'dashboard') await loadDashboardStats();
  };

  const handleToggleBan = async (user: AdminUserListItem) => {
    const confirmMsg = user.is_banned
      ? `Unban user "${user.first_name}"? Their account and mining will be restored.`
      : `Ban user "${user.first_name}"? They will be immediately blocked from logging in, viewing tasks, and claiming coins.`;
    if (!window.confirm(confirmMsg)) return;

    try {
      await adminApi.toggleBan(user.id, !user.is_banned);
      await loadUsers();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update account ban status');
    }
  };

  // Miner Actions
  const handleMinerAction = async (
    minerId: string,
    action: 'pause' | 'resume' | 'terminate',
    reason: string
  ) => {
    await adminApi.minerAction(minerId, action, reason);
    await loadMiners();
    if (currentTab === 'dashboard') await loadDashboardStats();
  };

  // Render Login Modal if not logged in
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-space-950 flex flex-col justify-center items-center p-4">
        <LoginModal onSuccess={handleLoginSuccess} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-space-950 flex flex-col selection:bg-purple-500/30 selection:text-purple-200">
      {/* Top Navigation Bar */}
      <AdminHeader
        currentTab={currentTab}
        onSelectTab={setCurrentTab}
        admin={currentAdmin}
        onLogout={handleLogout}
        onRefresh={handleGlobalRefresh}
        isLoading={isLoading || usersLoading || minersLoading}
      />

      {/* Main View Container */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6">
        {/* Global Error Banner */}
        {globalError && (
          <div className="mb-6 p-4 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-between text-rose-400 text-xs">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{globalError}</span>
            </div>
            <button
              onClick={handleGlobalRefresh}
              className="px-3 py-1 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 font-bold"
            >
              Retry
            </button>
          </div>
        )}

        {/* Dynamic Tab Views */}
        {currentTab === 'dashboard' && (
          stats ? (
            <DashboardView stats={stats} onNavigate={setCurrentTab} />
          ) : (
            <div className="py-24 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-400 mb-3" />
              <p className="text-sm text-gray-400 font-medium">Gathering ecosystem telemetry...</p>
            </div>
          )
        )}

        {currentTab === 'users' && (
          <UserTable
            users={usersData?.users || []}
            total={usersData?.total || 0}
            limit={usersLimit}
            offset={usersOffset}
            searchQuery={userSearchQuery}
            onSearchChange={(q) => {
              setUserSearchQuery(q);
              setUsersOffset(0);
            }}
            statusFilter={userStatusFilter}
            onStatusFilterChange={(s) => {
              setUserStatusFilter(s);
              setUsersOffset(0);
            }}
            onPageChange={(newOffset) => setUsersOffset(newOffset)}
            onInspectUser={(id) => setInspectingUserId(id)}
            onOpenAdjust={(u) => setAdjustingUser(u)}
            onToggleBan={handleToggleBan}
            isLoading={usersLoading}
          />
        )}

        {currentTab === 'miners' && (
          <MinersView
            miners={minersData?.miners || []}
            total={minersData?.total || 0}
            limit={minersLimit}
            offset={minersOffset}
            typeFilter={minerTypeFilter}
            onTypeFilterChange={(t) => {
              setMinerTypeFilter(t);
              setMinersOffset(0);
            }}
            statusFilter={minerStatusFilter}
            onStatusFilterChange={(s) => {
              setMinerStatusFilter(s);
              setMinersOffset(0);
            }}
            searchQuery={minerSearchQuery}
            onSearchChange={(q) => {
              setMinerSearchQuery(q);
              setMinersOffset(0);
            }}
            onPageChange={(newOffset) => setMinersOffset(newOffset)}
            onAction={handleMinerAction}
            isLoading={minersLoading}
          />
        )}

        {currentTab === 'settings' && <SettingsView />}

        {currentTab === 'payments' && <PaymentsView />}

        {currentTab === 'activity' && <ActivityLogsView />}
      </main>

      {/* User Detail Inspection Modal */}
      <UserDetailModal
        userId={inspectingUserId}
        onClose={() => setInspectingUserId(null)}
        onOpenAdjust={(u) => setAdjustingUser(u)}
        onToggleBan={handleToggleBan}
      />

      {/* Manual Balance Adjust Modal */}
      <BalanceAdjustModal
        user={adjustingUser}
        onClose={() => setAdjustingUser(null)}
        onSubmit={handleCommitAdjust}
      />
    </div>
  );
}
